import React from 'react';
import { Comment, User } from '../../types';
import styles from './CommentList.module.css';

interface CommentListProps {
  comments: Comment[];
  getAuthor: (id: string) => User | undefined;
  onReply?: (comment: Comment) => void;
  activeReplyToCommentId?: string;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString();
}

function sortByCreatedAt(a: Comment, b: Comment) {
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

interface CommentItemProps {
  comment: Comment;
  getAuthor: (id: string) => User | undefined;
  onReply?: (comment: Comment) => void;
  activeReplyToCommentId?: string;
  isReply?: boolean;
  replyTargetLabel?: string;
  showReplyButton?: boolean;
}

function CommentItem({
  comment,
  getAuthor,
  onReply,
  activeReplyToCommentId,
  isReply = false,
  replyTargetLabel,
  showReplyButton = true,
}: CommentItemProps) {
  const author = comment.author ?? getAuthor(comment.authorId);
  const isActiveReplyTarget = activeReplyToCommentId === comment.id;

  return (
    <div className={`${styles.commentCard} ${isReply ? styles.replyCard : ''} ${isActiveReplyTarget ? styles.activeReplyTarget : ''}`}>
      <div className={styles.commentHeader}>
        <span className={styles.authorName}>{author?.displayName ?? 'Unknown'}</span>
        <span className={styles.time}>{formatDate(comment.createdAt)}</span>
      </div>
      {replyTargetLabel && <span className={styles.replyingTo}>Replying to {replyTargetLabel}</span>}
      <p className={styles.body}>{comment.body}</p>
      {onReply && showReplyButton && (
        <button type="button" className={styles.replyButton} onClick={() => onReply(comment)}>
          Reply
        </button>
      )}
    </div>
  );
}

export function CommentList({ comments, getAuthor, onReply, activeReplyToCommentId }: CommentListProps) {
  if (comments.length === 0) return null;

  const commentsById = new Map(comments.map((comment) => [comment.id, comment]));
  const topLevelComments = comments
    .filter((comment) => !comment.parentCommentId || !commentsById.has(comment.parentCommentId))
    .sort(sortByCreatedAt);
  const repliesByParent = new Map<string, Comment[]>();

  comments
    .filter((comment) => comment.parentCommentId && commentsById.has(comment.parentCommentId))
    .sort(sortByCreatedAt)
    .forEach((comment) => {
      const parentId = comment.parentCommentId!;
      const list = repliesByParent.get(parentId);
      if (list) list.push(comment);
      else repliesByParent.set(parentId, [comment]);
    });

  return (
    <ul className={styles.list}>
      {topLevelComments.map((comment) => {
        const replies = repliesByParent.get(comment.id) ?? [];
        const lastReplyId = replies.length > 0 ? replies[replies.length - 1].id : undefined;
        return (
          <li key={comment.id} id={`comment-${comment.id}`} className={styles.item}>
            <CommentItem
              comment={comment}
              getAuthor={getAuthor}
              onReply={onReply}
              activeReplyToCommentId={activeReplyToCommentId}
              showReplyButton={replies.length === 0}
            />
            {replies.length > 0 && (
              <ul className={styles.replies}>
                {replies.map((reply) => {
                  const replyToAuthor = reply.replyToCommentId
                    ? commentsById.get(reply.replyToCommentId)?.author ?? getAuthor(commentsById.get(reply.replyToCommentId)?.authorId ?? '')
                    : undefined;

                  return (
                    <li key={reply.id} id={`comment-${reply.id}`} className={styles.replyItem}>
                      <CommentItem
                        comment={reply}
                        getAuthor={getAuthor}
                        onReply={onReply}
                        activeReplyToCommentId={activeReplyToCommentId}
                        isReply
                        replyTargetLabel={replyToAuthor?.displayName}
                        showReplyButton={reply.id === lastReplyId}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
